/**
 * Conversão MM <-> KG a partir da geometria do material.
 * Espelha as regras do backend (CalculateService) e do QuantityManager:
 *
 * - BARRA / REDONDO / ANEL: seção circular maciça (só dimensão 1).
 *   ANEL com dimensão 2 > 0 é tratado como vazado.
 * - TUBO: seção circular vazada (dimensão 1 externa, dimensão 2 interna).
 * - QUADRADO / CHAPA / LINGOTE: seção retangular (dimensão 1 x dimensão 2).
 * - Demais formatos (ex.: MEIA LUA): sem fórmula -> retorna null.
 *
 * Peso (kg) = área (mm²) × comprimento (mm) × densidade (g/cm³) / 1.000.000
 */

export interface StockGeometry {
  format?: string | null
  density?: string | number | null
  dimension1?: string | number | null
  dimension2?: string | number | null
}

const toNumber = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined) return NaN
  const n = Number(String(v).trim().replace(',', '.'))
  return Number.isFinite(n) ? n : NaN
}

/** Área da seção transversal em mm², ou null quando não há fórmula/dados. */
export function crossSectionAreaMm2(geometry: StockGeometry): number | null {
  const fmt = String(geometry.format ?? '').trim().toUpperCase()
  const d1 = toNumber(geometry.dimension1)
  const d2raw = toNumber(geometry.dimension2)
  const d2 = Number.isFinite(d2raw) ? d2raw : 0

  if (!fmt || !Number.isFinite(d1) || d1 <= 0) return null

  switch (fmt) {
    case 'BARRA':
    case 'REDONDO':
    case 'ANEL':
      if (fmt === 'ANEL' && d2 > 0) {
        if (d2 >= d1) return null
        return Math.PI * ((d1 / 2) ** 2 - (d2 / 2) ** 2)
      }
      return Math.PI * (d1 / 2) ** 2
    case 'TUBO':
      if (d2 <= 0 || d2 >= d1) return null
      return Math.PI * ((d1 / 2) ** 2 - (d2 / 2) ** 2)
    case 'QUADRADO':
    case 'CHAPA':
    case 'LINGOTE':
      if (d2 <= 0) return null
      return d1 * d2
    default:
      return null
  }
}

/** Converte um comprimento em mm para kg. Retorna null se não for possível calcular. */
export function mmToKg(lengthInMm: number, geometry: StockGeometry): number | null {
  const area = crossSectionAreaMm2(geometry)
  const dens = toNumber(geometry.density)
  if (area === null || !Number.isFinite(dens) || dens <= 0 || !Number.isFinite(lengthInMm)) return null
  return (area * lengthInMm * dens) / 1_000_000
}

/** Converte um peso em kg para mm. Retorna null se não for possível calcular. */
export function kgToMm(weightInKg: number, geometry: StockGeometry): number | null {
  const area = crossSectionAreaMm2(geometry)
  const dens = toNumber(geometry.density)
  if (area === null || !Number.isFinite(dens) || dens <= 0 || !Number.isFinite(weightInKg)) return null
  return (weightInKg * 1_000_000) / (area * dens)
}
